<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check, ChevronDown, Globe } from 'lucide-vue-next'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n'
import Popover from 'primevue/popover'
import LocaleFlag from '@/components/base/LocaleFlag.vue'

const ui = useUiStore()
const auth = useAuthStore()
const i18n = useI18n()
const popover = ref<InstanceType<typeof Popover> | null>(null)

const labels: Record<SupportedLocale, string> = { de: 'Deutsch', en: 'English', ru: 'Русский' }

async function select(l: SupportedLocale) {
  ui.locale = l
  i18n.locale.value = l
  popover.value?.hide()
  if (auth.isAuthenticated) {
    try {
      await auth.updateLocale(l)
    } catch {
      // backend-update darf nicht den UI-State blockieren
    }
  }
}
</script>

<template>
  <!-- Trigger bleibt das generische Globe-Icon — Flaggen erscheinen erst
       in der Sprach-Liste im Popover. -->
  <button
    type="button"
    class="inline-flex items-center gap-1 px-2 h-9 rounded-md border border-[var(--border)] hover:bg-[var(--surface-2)] text-[var(--text-2)] hover:text-[var(--text)]"
    :aria-label="$t('language')"
    @click="(e) => popover?.toggle(e)"
  >
    <Globe :size="18" />
    <ChevronDown :size="14" />
  </button>

  <Popover ref="popover">
    <div class="flex flex-col gap-1 min-w-[170px]">
      <button
        v-for="l in SUPPORTED_LOCALES"
        :key="l"
        type="button"
        class="flex items-center gap-2 px-3 py-2 rounded hover:bg-[var(--surface-2)] text-left"
        @click="select(l)"
      >
        <LocaleFlag :locale="l" />
        <span class="flex-1">{{ labels[l] }}</span>
        <Check v-if="ui.locale === l" :size="14" class="text-[var(--accent)]" />
      </button>
    </div>
  </Popover>
</template>
